import React from "react";
import { Helmet } from "react-helmet";
import {
  Shield,
  Building2,
  Database,
  User,
  Briefcase,
  Store,
} from "lucide-react";
import "./Legal.css";

export default function Terms() {
  return (
    <>
      <Helmet>
        <title>Terms & Conditions | Civilink Private Limited</title>

        <meta
          name="description"
          content="Terms and Conditions of Civilink Private Limited."
        />

        <link
          rel="canonical"
          href="https://www.civilink.in/terms"
        />
      </Helmet>

      <div className="legal-page">
      {/* Hero */}

      <section className="legal-hero">

        <Shield size={60} />

        <h1>Terms & Conditions</h1>

        <p>Civilink Private Limited</p>

        <span>Last Updated: July 29, 2026</span>

      </section>

      {/* Acceptance */}

      <section className="legal-card">

        <div className="legal-title">

          <Shield size={26} />

          <h2>1. Acceptance of Terms</h2>

        </div>

        <p>

          Welcome to <strong>Civilink Private Limited</strong>
          ("Civilink", "Company", "we", "our", or "us").

        </p>

        <p>

          These Terms and Conditions govern your access to and use
          of the Civilink website, mobile applications, marketplace,
          products, services, and all related features
          (collectively referred to as the "Platform").

        </p>

        <p>

          By accessing or using the Platform, you agree to be bound
          by these Terms and Conditions, our Privacy Policy,
          and any additional policies published by Civilink.

        </p>

        <p>

          If you do not agree with these Terms,
          you must discontinue use of the Platform.

        </p>

      </section>

      {/* Definitions */}

      <section className="legal-card">

        <div className="legal-title">

          <Database size={26} />

          <h2>2. Definitions</h2>

        </div>

        <ul>

          <li><strong>Platform</strong> means the Civilink website, mobile applications and related services.</li>

          <li><strong>User</strong> means any individual or organization accessing the Platform.</li>

          <li><strong>Vendor</strong> means a supplier, dealer, manufacturer, distributor or business listing products or services.</li>

          <li><strong>Professional</strong> means engineers, architects, contractors, interior designers and other industry professionals.</li>

          <li><strong>Content</strong> includes text, photographs, videos, documents, reviews, ratings and listings.</li>

          <li><strong>Services</strong> means the digital services provided by Civilink.</li>

        </ul>

      </section>

      {/* About */}

      <section className="legal-card">

        <div className="legal-title">

          <Building2 size={26} />

          <h2>3. About Civilink</h2>

        </div>

        <p>

          Civilink Private Limited is an Indian Construction
          Technology (ConTech) company that operates a digital
          marketplace connecting construction industry participants.

        </p>

        <p>

          The Platform enables users to discover products,
          professionals, vendors, suppliers, rental providers,
          contractors, architects, engineers, property listings,
          and construction-related services.

        </p>

        <p>

          Civilink provides technology services only and facilitates
          communication and marketplace interactions between users.

        </p>

      </section>

      {/* Platform Nature */}

      <section className="legal-card">

        <div className="legal-title">

          <Store size={26} />

          <h2>4. Nature of the Platform</h2>

        </div>

        <p>

          Civilink is a technology platform and marketplace.

        </p>

        <p>

          Unless expressly stated otherwise, Civilink is not:

        </p>

        <ul>

          <li>A manufacturer</li>

          <li>A supplier</li>

          <li>A contractor</li>

          <li>An architect</li>

          <li>An engineer</li>

          <li>A transporter</li>

          <li>A warehouse provider</li>

          <li>A payment bank</li>

          <li>A real estate broker</li>

          <li>A property owner</li>

          <li>An employer of listed professionals</li>

        </ul>

        <p>

          Civilink does not become a party to agreements,
          negotiations, quotations, or contracts entered into
          between users unless expressly stated otherwise.

        </p>

      </section>

      {/* Eligibility */}

      <section className="legal-card">

        <div className="legal-title">

          <User size={26} />

          <h2>5. Eligibility</h2>

        </div>

        <p>

          You must be legally capable of entering into binding
          agreements under applicable law to use the Platform.

        </p>

        <p>

          If you are using the Platform on behalf of a company,
          organization or business, you represent that you have
          authority to bind that entity to these Terms.

        </p>

      </section>

      {/* Accounts */}

      <section className="legal-card">

        <div className="legal-title">

          <User size={26} />

          <h2>6. User Accounts</h2>

        </div>

        <p>

          Users are responsible for maintaining accurate,
          complete and current account information.

        </p>

        <p>

          You agree not to create accounts using false identities,
          misleading information or unauthorized credentials.

        </p>

        <p>

          Civilink reserves the right to suspend, restrict or
          terminate accounts that violate these Terms.

        </p>

      </section>

      {/* Account Security */}

      <section className="legal-card">

        <div className="legal-title">

          <Shield size={26} />

          <h2>7. Account Security</h2>

        </div>

        <p>

          You are solely responsible for maintaining the
          confidentiality of your account credentials.

        </p>

        <ul>

          <li>Keep your password confidential.</li>

          <li>Do not share OTPs or verification codes.</li>

          <li>Notify Civilink immediately if unauthorized access is suspected.</li>

        </ul>

      </section>

      {/* Marketplace */}

      <section className="legal-card">

        <div className="legal-title">

          <Store size={26} />

          <h2>8. Marketplace Services</h2>

        </div>

        <p>

          Civilink enables users to publish listings,
          discover products,
          obtain quotations,
          communicate with vendors,
          and explore construction-related opportunities.

        </p>

        <p>

          Civilink does not guarantee that products,
          services or listings will meet your expectations
          or requirements.

        </p>

      </section>

      {/* Vendor */}

      <section className="legal-card">

        <div className="legal-title">

          <Briefcase size={26} />

          <h2>9. Vendor & Professional Services</h2>

        </div>

        <p>

          Vendors and professionals are independent users
          of the Platform.

        </p>

        <p>

          They are solely responsible for:

        </p>

        <ul>

          <li>Business licenses</li>

          <li>Professional qualifications</li>

          <li>Product information</li>

          <li>Pricing</li>

          <li>Availability</li>

          <li>Quality of products</li>

          <li>Delivery commitments</li>

          <li>Compliance with applicable laws</li>

        </ul>

      </section>

      {/* Listings */}

      <section className="legal-card">

        <div className="legal-title">

          <Database size={26} />

          <h2>10. Listings and Content</h2>

        </div>

        <p>

          Users are solely responsible for all information,
          photographs, videos, descriptions, pricing,
          documents and other content they publish.

        </p>

        <p>

          By submitting content to Civilink,
          you represent that you have the necessary rights
          to publish such content.

        </p>

        <p>

          Civilink reserves the right to edit,
          remove,
          reject,
          restrict or disable content that violates
          these Terms,
          applicable law,
          or the rights of others.

        </p>

      </section>
            {/* Acceptable Use */}

      <section className="legal-card">

        <div className="legal-title">

          <Shield size={26} />

          <h2>11. Acceptable Use</h2>

        </div>

        <p>

          You agree to use the Platform only for lawful purposes and
          in accordance with these Terms and applicable laws.

        </p>

        <p>

          Users shall respect the rights of other users and shall not
          interfere with the normal operation, security, or integrity
          of the Platform.

        </p>

      </section>

      {/* Prohibited Activities */}

      <section className="legal-card">

        <div className="legal-title">

          <Shield size={26} />

          <h2>12. Prohibited Activities</h2>

        </div>

        <p>

          Users shall not engage in activities including but not
          limited to:

        </p>

        <ul>

          <li>Providing false or misleading information.</li>

          <li>Impersonating another individual or organization.</li>

          <li>Posting illegal, offensive, fraudulent or misleading content.</li>

          <li>Violating intellectual property rights.</li>

          <li>Uploading viruses, malware or harmful software.</li>

          <li>Attempting unauthorized access to the Platform.</li>

          <li>Manipulating ratings, reviews or marketplace listings.</li>

          <li>Using automated bots or scraping tools without permission.</li>

          <li>Violating applicable laws or regulations.</li>

        </ul>

      </section>

      {/* Orders */}

      <section className="legal-card">

        <div className="legal-title">

          <Store size={26} />

          <h2>13. Orders and Quotations</h2>

        </div>

        <p>

          Civilink may enable users to request quotations,
          communicate with vendors, or place orders through the
          Platform.

        </p>

        <p>

          Quotations, prices, availability, delivery schedules,
          specifications and other commercial terms are determined
          solely by the respective vendor unless expressly stated
          otherwise.

        </p>

        <p>

          Civilink is not responsible for negotiations or contractual
          obligations between users.

        </p>

      </section>

      {/* Payments */}

      <section className="legal-card">

        <div className="legal-title">

          <Database size={26} />

          <h2>14. Payments</h2>

        </div>

        <p>

          Certain services may require payment through approved
          payment service providers.

        </p>

        <p>

          Payment processing is handled by third-party payment
          providers. Civilink does not store complete debit card,
          credit card, CVV, UPI PIN or internet banking credentials.

        </p>

        <p>

          Users are responsible for ensuring sufficient funds,
          payment authorization and compliance with applicable
          payment regulations.

        </p>

      </section>

      {/* Taxes */}

      <section className="legal-card">

        <div className="legal-title">

          <Database size={26} />

          <h2>15. Taxes</h2>

        </div>

        <p>

          Vendors and service providers are solely responsible for
          determining, collecting, reporting and paying any taxes,
          duties, levies or statutory charges applicable to their
          business activities.

        </p>

        <p>

          Civilink does not provide tax advice and shall not be
          responsible for any tax liabilities arising between users.

        </p>

      </section>

      {/* Delivery */}

      <section className="legal-card">

        <div className="legal-title">

          <Store size={26} />

          <h2>16. Delivery and Fulfilment</h2>

        </div>

        <p>

          Delivery timelines, transportation, packaging,
          installation, unloading and related logistics are the
          responsibility of the respective vendor or service provider
          unless expressly stated otherwise.

        </p>

        <p>

          Civilink does not guarantee delivery dates or the condition
          of products delivered by third parties.

        </p>

      </section>

      {/* Returns */}

      <section className="legal-card">

        <div className="legal-title">

          <Store size={26} />

          <h2>17. Returns and Refunds</h2>

        </div>

        <p>

          Return, replacement and refund policies are determined by
          the respective vendor unless Civilink expressly states
          otherwise for a particular service.

        </p>

        <p>

          Users should carefully review the vendor's applicable
          policies before completing any transaction.

        </p>

      </section>

      {/* Cancellation */}

      <section className="legal-card">

        <div className="legal-title">

          <Store size={26} />

          <h2>18. Cancellation</h2>

        </div>

        <p>

          Orders, bookings or service requests may be cancelled in
          accordance with the policies of the respective vendor or
          service provider.

        </p>

        <p>

          Civilink reserves the right to cancel or suspend
          transactions where fraud, misuse, legal violations or
          security concerns are suspected.

        </p>

      </section>

      {/* Third Party Services */}

      <section className="legal-card">

        <div className="legal-title">

          <Building2 size={26} />

          <h2>19. Third-Party Services</h2>

        </div>

        <p>

          The Platform may integrate or provide access to third-party
          services including payment providers, maps, cloud hosting,
          messaging services and social media platforms.

        </p>

        <p>

          Civilink is not responsible for the availability, content,
          privacy practices or performance of third-party services.

        </p>

      </section>

      {/* Verification */}

      <section className="legal-card">

        <div className="legal-title">

          <User size={26} />

          <h2>20. Verification</h2>

        </div>

        <p>

          Civilink may verify certain users, vendors or businesses
          based on documents or information voluntarily submitted.

        </p>

        <p>

          Verification indicates that specified information has been
          reviewed at a particular point in time. It does not
          constitute a guarantee, endorsement, certification or
          warranty regarding identity, qualifications, ownership,
          product quality or future conduct.

        </p>

      </section>

            {/* Intellectual Property */}

      <section className="legal-card">

        <div className="legal-title">

          <Database size={26} />

          <h2>21. Intellectual Property</h2>

        </div>

        <p>

          Unless otherwise stated, the Platform, including its
          software, design, logos, trademarks, graphics, text,
          databases and other proprietary materials are owned by
          or licensed to Civilink Private Limited and are protected
          by applicable intellectual property laws.

        </p>

        <p>

          Users shall not copy, reproduce, distribute, modify,
          reverse engineer or commercially exploit any part of the
          Platform without prior written permission from Civilink.

        </p>

      </section>

      {/* User Generated Content */}

      <section className="legal-card">

        <div className="legal-title">

          <User size={26} />

          <h2>22. User Generated Content</h2>

        </div>

        <p>

          Users retain ownership of content they submit to the
          Platform.

        </p>

        <p>

          By uploading or publishing content, you grant Civilink a
          worldwide, non-exclusive, royalty-free license to host,
          display, reproduce, distribute and use such content for
          operating, promoting and improving the Platform.

        </p>

        <p>

          You represent that your content does not infringe any
          third-party rights.

        </p>

      </section>

      {/* Disclaimer */}

      <section className="legal-card">

        <div className="legal-title">

          <Shield size={26} />

          <h2>23. Disclaimer of Warranties</h2>

        </div>

        <p>

          The Platform is provided on an "AS IS" and "AS AVAILABLE"
          basis.

        </p>

        <p>

          Civilink makes no representation or warranty regarding:

        </p>

        <ul>

          <li>Product quality</li>

          <li>Construction materials</li>

          <li>Vendor reliability</li>

          <li>Professional qualifications</li>

          <li>Property ownership</li>

          <li>Construction outcomes</li>

          <li>Delivery timelines</li>

          <li>Availability of services</li>

          <li>Accuracy of user-generated information</li>

        </ul>

      </section>

      {/* Liability */}

      <section className="legal-card">

        <div className="legal-title">

          <Shield size={26} />

          <h2>24. Limitation of Liability</h2>

        </div>

        <p>

          To the maximum extent permitted by law, Civilink Private
          Limited, its directors, employees, officers and affiliates
          shall not be liable for any indirect, incidental, special,
          consequential or punitive damages arising out of or related
          to the use of the Platform.

        </p>

        <p>

          Civilink shall not be responsible for disputes between
          buyers, sellers, vendors, contractors, professionals,
          property owners or any third parties using the Platform.

        </p>

      </section>

      {/* Indemnity */}

      <section className="legal-card">

        <div className="legal-title">

          <Shield size={26} />

          <h2>25. Indemnification</h2>

        </div>

        <p>

          You agree to indemnify, defend and hold harmless
          Civilink Private Limited, its directors, officers,
          employees and affiliates from claims, damages,
          liabilities, costs and expenses arising from:

        </p>

        <ul>

          <li>Your use of the Platform.</li>

          <li>Your violation of these Terms.</li>

          <li>Your violation of applicable laws.</li>

          <li>Your infringement of third-party rights.</li>

        </ul>

      </section>

      {/* Suspension */}

      <section className="legal-card">

        <div className="legal-title">

          <User size={26} />

          <h2>26. Suspension and Termination</h2>

        </div>

        <p>

          Civilink may suspend, restrict or permanently terminate
          user accounts without prior notice where necessary to
          protect the Platform, comply with legal obligations,
          investigate suspected violations or maintain platform
          security.

        </p>

      </section>

      {/* Privacy */}

      <section className="legal-card">

        <div className="legal-title">

          <Database size={26} />

          <h2>27. Privacy</h2>

        </div>

        <p>

          Your use of the Platform is also governed by the Civilink
          Privacy Policy, which explains how personal information
          is collected, used and protected.

        </p>

      </section>

      {/* Force Majeure */}

      <section className="legal-card">

        <div className="legal-title">

          <Building2 size={26} />

          <h2>28. Force Majeure</h2>

        </div>

        <p>

          Civilink shall not be liable for delays or failures caused
          by events beyond its reasonable control, including natural
          disasters, war, civil unrest, government actions,
          telecommunications failures, internet outages,
          cyberattacks or other force majeure events.

        </p>

      </section>

      {/* Governing Law */}

      <section className="legal-card">

        <div className="legal-title">

          <Building2 size={26} />

          <h2>29. Governing Law</h2>

        </div>

        <p>

          These Terms shall be governed by and interpreted in
          accordance with the laws of the Republic of India.

        </p>

      </section>

      {/* Dispute Resolution */}

      <section className="legal-card">

        <div className="legal-title">

          <Shield size={26} />

          <h2>30. Dispute Resolution</h2>

        </div>

        <p>

          Users agree to first attempt to resolve disputes through
          good-faith discussions with Civilink.

        </p>

        <p>

          Subject to applicable law, courts having jurisdiction in
          Tamil Nadu, India shall have exclusive jurisdiction over
          disputes relating to these Terms.

        </p>

      </section>

      {/* Changes */}

      <section className="legal-card">

        <div className="legal-title">

          <Database size={26} />

          <h2>31. Changes to these Terms</h2>

        </div>

        <p>

          Civilink may revise these Terms from time to time.

        </p>

        <p>

          Updated Terms will be published on the Platform together
          with the revised "Last Updated" date.

        </p>

        <p>

          Continued use of the Platform constitutes acceptance of
          the updated Terms.

        </p>

      </section>

      {/* Contact */}

      <section className="legal-card">

        <div className="legal-title">

          <Building2 size={26} />

          <h2>32. Contact Information</h2>

        </div>

        <p>

          For questions regarding these Terms & Conditions, please
          contact us:

        </p>

        <div className="contact-grid">

          <a
            className="contact-btn"
            href="https://www.civilink.in"
            target="_blank"
            rel="noreferrer"
          >
            <strong>Website</strong>
            <span>www.civilink.in</span>
          </a>

          <a
            className="contact-btn"
            href="mailto:civilink.official@gmail.com"
          >
            <strong>Email</strong>
            <span>civilink.official@gmail.com</span>
          </a>

          <a
            className="contact-btn"
            href="https://wa.me/919344425095"
            target="_blank"
            rel="noreferrer"
          >
            <strong>WhatsApp</strong>
            <span>+91 93444 25095</span>
          </a>

        </div>

      </section>

      {/* Footer */}

      <footer className="about-footer">

        <h3>Civilink Private Limited</h3>

        <p className="footer-tagline">

          Building India's Trusted Digital Construction Ecosystem

        </p>

        <p className="footer-text">

          These Terms & Conditions govern your use of the Civilink
          Platform. By continuing to use our services, you agree to
          comply with these Terms and all applicable laws.

        </p>

        <div className="footer-divider"></div>

        <p className="footer-version">

          Terms & Conditions Version 1.0.0

        </p>

        <p className="footer-copy">

          © 2026 Civilink Private Limited

          <br />

          All Rights Reserved.

        </p>

      </footer>

      </div>
    </>
  );
}